import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { Space, Tooltip } from 'antd';
import TableComponent from '../antTable/table';
import CustomPagination from '../antTable/pagenation';
import { SweetSweetAlert } from 'component/sweetalert_components';
import ViewOuProperties from 'component/modal_components/viewOuProperties';
import { SpanButton } from 'component/button_components/button.style';
import { ReactComponent as DeleteIcon } from 'assets/images/SVG/icons/delete-icon.svg';
import UpdateouPropertiesModal from 'component/modal_components/update_ou_properties';
import { useRefreshOuTab } from './refreshOuData';
import {
  deleteOuData,
  setOuIsDescending,
  setOuSortType,
  setPageOuNumber,
  setPageOuSize,
} from 'redux/slices/getChildOu';
import { deleteOuInAdminTree } from 'redux/slices/treedata';
import { deleteOuInOuAdminTree } from 'redux/slices/ouAdminTree';
import ModalOpenLoader from 'component/loader_component/modalOpenLoader';
import TableLoader from 'component/loader_component/tableLoader';
import { refreshToken } from 'redux/slices/authSlice';

export default function OuData() {
  const refreshOuTab = useRefreshOuTab();
  const { data, loading, pageOuNumber, pageOuSize, totalCount } = useSelector((state) => state.getChildOuData);
  const dispatch = useDispatch();
  const selectedNode = useSelector((state) => state.selectedNode);
  const { ouAdminRights, portalAdminRights } = useSelector((state) => state.settings);
  const [loadingApi, setLoadingApi] = useState(false);
  const role = localStorage.getItem('role');
  const fqdnname = selectedNode.domainName;
  const isSuperAdmin = role === 'superadmin';
  const canView =
    isSuperAdmin ||
    (role === 'portaladmin' ? portalAdminRights?.portalouView ?? true : ouAdminRights?.ouAdminouView ?? true);
  const canEdit =
    isSuperAdmin ||
    (role === 'portaladmin' ? portalAdminRights?.portalouEdit ?? true : ouAdminRights?.ouAdminouEdit ?? true);
  const canDelete =
    isSuperAdmin ||
    (role === 'portaladmin' ? portalAdminRights?.portalouDelete ?? true : ouAdminRights?.ouAdminouDelete ?? true);

  const deleteDomainSweetAlert = async (dnName) => {
    await dispatch(refreshToken());
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Are you sure you want to delete?',
      showConfirmButton: true,
      confirmButtonColor: 'var(--bg-button-default-color)',
      width: '400px',
      heightAuto: false,
      showCancelButton: true,
      cancelButtonColor: 'green',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        container: 'custom-swal-container',
        icon: 'custom-swal-icon',
        title: 'custom-swal-title',
      },
    });

    if (result.isConfirmed) {
      try {
        const sweetAlert = await dispatch(deleteOuData({ ou: dnName, fqdn: fqdnname }));
        setLoadingApi(true);
        if (sweetAlert.payload && sweetAlert.payload.status === 200) {
          if (localStorage.getItem('role') === 'ouadmin') {
            await dispatch(deleteOuInOuAdminTree(dnName));
          } else {
            await dispatch(deleteOuInAdminTree(dnName));
          }
          SweetSweetAlert({ sweetData: sweetAlert.payload.data, icon: 'success', buttonColor: 'green' });
          refreshOuTab();
        } else if (sweetAlert.payload && sweetAlert.payload.response.status === 404) {
          SweetSweetAlert({ text: sweetAlert.payload.response.data, icon: 'info', buttonColor: 'blue' });
        } else {
          SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
        }
      } catch (error) {
        SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
      } finally {
        setLoadingApi(false);
      }
    }
  };

  const columns = [
    {
      title: 'OU Name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <Space>
          {canEdit && <UpdateouPropertiesModal {...record} />}
          {canView && <ViewOuProperties {...record} />}
          {canDelete && (
            <Tooltip title='Delete' color='#597081' className='af-ou-delete-tooltip'>
              <SpanButton className='af-ou-delete-tooltip' onClick={() => deleteDomainSweetAlert(record.dnName)}>
                <DeleteIcon className='af-ou-delete-tooltip' />
              </SpanButton>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const handleSorting = (_, __, sorter) => {
    if (sorter.order) {
      const isDescending = sorter.order === 'descend';
      dispatch(setOuIsDescending(isDescending));
      dispatch(setOuSortType(sorter.field));
    } else {
      dispatch(setOuSortType(''));
    }
  };

  const startIndex = (pageOuNumber - 1) * pageOuSize;
  const endIndex = startIndex + pageOuSize;

  const handlePagination = async (value, pagination) => {
    dispatch(setPageOuNumber(value));
    dispatch(setPageOuSize(pagination));
  };

  return (
    <div>
      {loadingApi && <ModalOpenLoader />}
      <TableComponent
        columns={columns}
        data={data}
        onChange={handleSorting}
        loading={{
          spinning: loading,
          indicator: <TableLoader />,
        }}
      />
      <CustomPagination
        loading={loading}
        totalItems={totalCount}
        currentPage={pageOuNumber}
        pageSize={pageOuSize}
        onPageChange={handlePagination}
        pageSizeOptions={[20, 50, 100, 500]}
        startIndex={startIndex}
        endIndex={endIndex}
      />
    </div>
  );
}
